import React, { useState, useEffect } from 'react';
import './WebPageImages.css';
import { MdAdd, MdEdit, MdDelete, MdImage, MdSave, MdClose, MdRefresh } from 'react-icons/md';
import { FaUpload, FaEye, FaDownload } from 'react-icons/fa';
import webPageImagesService from '../../services/webPageImages';
import LoadingSpinner from '../common/LoadingSpinner';
import ShadcnActionDropdown from '../common/ShadcnActionDropdown/ShadcnActionDropdown';

const WebPageImages = () => {
  const [sections, setSections] = useState([]);

  const [selectedSection, setSelectedSection] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSectionPreview, setShowSectionPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [newImage, setNewImage] = useState({
    name: '',
    url: '',
    alt: '',
    description: '',
    file: null
  });

  const filteredSections = sections.filter(section => {
    if (filter === 'all') return true;
    return section.category === filter;
  });

  const handleAddImage = async () => {
    if (selectedSection && newImage.name && newImage.file) {
      try {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', newImage.file);
        formData.append('name', newImage.name);
        formData.append('alt', newImage.alt || newImage.name);
        formData.append('description', newImage.description || '');
        formData.append('section', selectedSection.id);
        formData.append('category', selectedSection.category);
        // For gallery sections, use the section's imageType, otherwise default to 'banner'
        const imageType = selectedSection.imageType || 'banner';
        formData.append('imageType', imageType);

        const response = await webPageImagesService.uploadImage(formData, (progress) => {
          setUploadProgress(progress);
        });

        if (response && response.success) {
          // Refresh all sections and images
          await loadSectionsAndImages();
          setNewImage({ name: '', url: '', alt: '', description: '', file: null });
          setShowAddModal(false);
          // Show success message
          alert('Image uploaded successfully!');
        } else {
          setError(response?.message || 'Upload failed. Please try again.');
        }
      } catch (error) {
        console.error('Error adding image:', error);

        // Handle specific error types
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          setError('Upload timeout. Please try again with a smaller file or check your connection.');
        } else if (error.response?.status === 413) {
          setError('File too large. Please select a smaller image (max 10MB).');
        } else if (error.response?.status === 400) {
          setError(error.response.data?.message || 'Invalid file. Please check the file type and try again.');
        } else if (error.response?.status === 408) {
          setError('Upload interrupted. Please try again.');
        } else {
          setError('Upload failed. Please try again later.');
        }
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    } else {
      setError('Please provide a name and select a file.');
    }
  };

  const handleEditImage = async () => {
    if (editingImage && editingImage.name) {
      try {
        const updateData = {
          name: editingImage.name,
          alt: editingImage.alt,
          description: editingImage.description
        };

        await webPageImagesService.updateImage(editingImage.id, updateData);

        // Refresh all sections and images to update the UI
        await loadSectionsAndImages();
        setEditingImage(null);
      } catch (error) {
        console.error('Error updating image:', error);
        alert('Failed to update image. Please try again.');
      }
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await webPageImagesService.deleteImage(imageId);

        // Refresh all sections and images to update the UI
        await loadSectionsAndImages();
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('Failed to delete image. Please try again.');
      }
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewImage(prev => ({
        ...prev,
        file: file,
        url: URL.createObjectURL(file) // For preview
      }));
    }
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  // Load sections and images on component mount
  useEffect(() => {
    loadSectionsAndImages();
  }, []);

  const loadSectionsAndImages = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load sections from API
      const sectionsData = await webPageImagesService.getAllSections();
      let sectionsToUse = [];

      if (sectionsData && sectionsData.length > 0) {
        // Transform API data to match our component structure
        sectionsToUse = sectionsData.map(section => ({
          id: section.section,
          name: section.section.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          category: section.category,
          imageType: section.imageType, // Add imageType for gallery sections
          images: [] // Will be populated when we load images for each section
        }));
      } else {
        // If no sections from API, log it
        console.log('No sections from API');
        sectionsToUse = [];
      }

      // Load images for all sections
      const sectionsWithImages = await Promise.all(
        sectionsToUse.map(async (section) => {
          try {
            const imagesData = await webPageImagesService.getSectionImages(section.id);
            if (imagesData && imagesData.length > 0) {
              const transformedImages = imagesData.map(img => ({
                id: img.id,
                name: img.name,
                url: img.s3Url,
                alt: img.alt,
                description: img.description,
                imageType: img.imageType // Include imageType for gallery images
              }));
              return { ...section, images: transformedImages };
            }
          } catch (err) {
            console.error('Error loading images for section:', section.id, err);
          }
          return { ...section, images: [] };
        })
      );

      setSections(sectionsWithImages);
      setLoading(false);
    } catch (err) {
      console.error('Error loading sections and images:', err);
      setError('Failed to load sections and images');
      setLoading(false);
    }
  };

  const loadSections = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load sections from API
      const sectionsData = await webPageImagesService.getAllSections();
      if (sectionsData && sectionsData.length > 0) {
        // Transform API data to match our component structure
        const transformedSections = sectionsData.map(section => ({
          id: section.section,
          name: section.section.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          category: section.category,
          imageType: section.imageType, // Add imageType for gallery sections
          images: [] // Will be populated when we load images for each section
        }));
        setSections(transformedSections);
      } else {
        // If no sections from API, use default sections
        console.log('No sections from API, using default sections');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading sections:', err);
      setError('Failed to load sections');
      setLoading(false);
    }
  };

  const downloadImage = (image) => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = image.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };



  return (
    <div className="webpage-images">
      <div className="header">
        <h1 className="text-base font-bold">Web Page Images Management</h1>

        <button
          className="preview-btn"
          onClick={() => setShowSectionPreview(!showSectionPreview)}
        >
          {showSectionPreview ? 'Hide' : 'Show'} Section Preview
        </button>
      </div>

      {/* Section Preview */}
      {showSectionPreview && (
        <div className="section-preview">
          <h3>📋 Available Sections & Their Usage</h3>
          <div className="preview-grid">
            <div className="preview-card">
              <h4>🛒 E-commerce Landing Page</h4>
              <p><strong>Section:</strong> <code>e-commerce-landing</code></p>
              <p><strong>Usage:</strong> Hero carousel images on main e-commerce site</p>
              <p><strong>Status:</strong> ✅ Integrated with frontend</p>
              <p><strong>API Endpoint:</strong> <code>/api/v1/webpage-images/section/e-commerce-landing/active</code></p>
            </div>

            <div className="preview-card">
              <h4>🏢 Vendor Landing Page</h4>
              <p><strong>Section:</strong> <code>vendor-landing</code></p>
              <p><strong>Usage:</strong> Hero, background, and logo images on vendor platform</p>
              <p><strong>Status:</strong> ✅ Integrated with frontend</p>
              <p><strong>API Endpoint:</strong> <code>/api/v1/webpage-images/section/vendor-landing/active</code></p>
            </div>

            <div className="preview-card">
              <h4>🖼️ Gallery Categories</h4>
              <p><strong>Sections:</strong> <code>gallery</code> with imageType categories</p>
              <p><strong>Categories:</strong> operations, pickups, facilities, team, community, impact</p>
              <p><strong>Usage:</strong> Company operations, pickups, facilities, team, community, and impact photos</p>
              <p><strong>Status:</strong> ✅ Integrated with frontend</p>
              <p><strong>API Endpoint:</strong> <code>/api/v1/webpage-images/section/gallery/active</code></p>
            </div>

            <div className="preview-card">
              <h4>🔐 Authentication Pages</h4>
              <p><strong>Section:</strong> <code>auth-pages</code></p>
              <p><strong>Usage:</strong> Background images for login/register pages</p>
              <p><strong>Status:</strong> ⏳ Ready for integration</p>
              <p><strong>API Endpoint:</strong> <code>/api/v1/webpage-images/section/auth-pages/active</code></p>
            </div>

            <div className="preview-card">
              <h4>📦 Product Pages</h4>
              <p><strong>Section:</strong> <code>product-pages</code></p>
              <p><strong>Usage:</strong> Product images and gallery placeholders</p>
              <p><strong>Status:</strong> ⏳ Ready for integration</p>
              <p><strong>API Endpoint:</strong> <code>/api/v1/webpage-images/section/product-pages/active</code></p>
            </div>
          </div>
        </div>
      )}

      <div className="controls">
        <div className="filter-controls">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Categories</option>
            <option value="e-commerce">E-commerce</option>
            <option value="vendor">Vendor</option>
            <option value="gallery">Gallery</option>
            <option value="shared">Shared</option>
          </select>
        </div>
        <div className="action-controls">
          <button onClick={loadSectionsAndImages} className="refresh-btn" disabled={loading}>
            <MdRefresh />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <LoadingSpinner
          size="medium"
          message={`Error: ${error}`}
          color="danger"
        />
      )}

      {loading && (
        <LoadingSpinner
          size="large"
          message="Loading sections and images..."
        />
      )}

      <div className="sections-grid">
        {filteredSections.map(section => (
          <div key={section.id} className="section-card">
            <div className="section-header">
              <h3>{section.name}</h3>
              <div className="badges">
                <span className={`category-badge ${section.category}`}>
                  {section.category}
                </span>
                {section.imageType && (
                  <span className={`image-type-badge ${section.imageType}`}>
                    {section.imageType}
                  </span>
                )}
              </div>
            </div>

            <div className="images-grid">
              {section.images.map(image => (
                <div key={image.id} className="image-card">
                  <div className="image-preview">
                    <img src={image.url} alt={image.alt} />
                    <div className="image-overlay">
                      <ShadcnActionDropdown
                        size="medium"
                        variant="primary"
                        isImageCard={true}
                        actions={[
                          {
                            label: 'View Image',
                            icon: 'view',
                            onClick: () => openImageModal(image),
                            tooltip: 'View full image'
                          },
                          {
                            label: 'Edit Image',
                            icon: 'edit',
                            onClick: () => setEditingImage(image),
                            tooltip: 'Edit image details'
                          },
                          {
                            label: 'Download Image',
                            icon: 'download',
                            onClick: () => downloadImage(image),
                            tooltip: 'Download image file'
                          },
                          {
                            label: 'Delete Image',
                            icon: 'delete',
                            onClick: () => handleDeleteImage(image.id),
                            danger: true,
                            tooltip: 'Delete this image'
                          }
                        ]}
                      />
                    </div>
                  </div>
                  <div className="image-info">
                    <h4>{image.name}</h4>
                    <p>{image.description}</p>
                  </div>
                </div>
              ))}

              <div className="add-image-card" onClick={() => {
                setSelectedSection(section);
                setShowAddModal(true);
              }}>
                <MdAdd />
                <span>Add Image</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Image Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Image to {selectedSection?.name}</h3>
              <button onClick={() => setShowAddModal(false)}>
                <MdClose />
              </button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <p>⚠️ {error}</p>
                </div>
              )}
              <div className="form-group">
                <label>Image Name</label>
                <input
                  type="text"
                  value={newImage.name}
                  onChange={(e) => setNewImage(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter image name"
                />
              </div>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  value={newImage.url}
                  onChange={(e) => setNewImage(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="Enter image URL"
                />
              </div>
              <div className="form-group">
                <label>Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </div>
              <div className="form-group">
                <label>Alt Text</label>
                <input
                  type="text"
                  value={newImage.alt}
                  onChange={(e) => setNewImage(prev => ({ ...prev, alt: e.target.value }))}
                  placeholder="Enter alt text"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newImage.description}
                  onChange={(e) => setNewImage(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter image description"
                />
              </div>
            </div>
            <div className="modal-footer">
              {loading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{uploadProgress}%</span>
                </div>
              )}
              <button onClick={() => setShowAddModal(false)} disabled={loading}>Cancel</button>
              <button onClick={handleAddImage} className="primary" disabled={loading}>
                {loading ? 'Uploading...' : 'Add Image'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Image Modal */}
      {editingImage && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Image</h3>
              <button onClick={() => setEditingImage(null)}>
                <MdClose />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Image Name</label>
                <input
                  type="text"
                  value={editingImage.name}
                  onChange={(e) => setEditingImage(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  value={editingImage.url}
                  onChange={(e) => setEditingImage(prev => ({ ...prev, url: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Alt Text</label>
                <input
                  type="text"
                  value={editingImage.alt}
                  onChange={(e) => setEditingImage(prev => ({ ...prev, alt: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editingImage.description}
                  onChange={(e) => setEditingImage(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setEditingImage(null)}>Cancel</button>
              <button onClick={handleEditImage} className="primary">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>{selectedImage?.name}</h3>
              <button onClick={() => setShowImageModal(false)}>
                <MdClose />
              </button>
            </div>
            <div className="image-modal-body">
              <img src={selectedImage?.url} alt={selectedImage?.alt} />
              <div className="image-details">
                <p><strong>Alt Text:</strong> {selectedImage?.alt}</p>
                <p><strong>Description:</strong> {selectedImage?.description}</p>
                <p><strong>URL:</strong> {selectedImage?.url}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebPageImages;
